import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  fetchYearlyBookwiseReport,
  fetchAllYearwiseBookwiseReport,
} from "../../../redux/reportSummarySlice";
import YearlySummaryPage from "./YearlySummaryPage";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { logger } from "../../../utils/logger";

const YearlySummaryFilter = () => {
  const [setYearWise, setSetYearWise] = React.useState(false);
  const [fromdate, setFromdate] = useState(dayjs().month(3).date(1));
  const [todate, setTodate] = useState(null);
  const [isClicked, setIsClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [summary, setSummary] = useState([]);
  const [summaryType, setSummaryType] = useState("");
  const { user } = useSelector((state) => state.auth);

  const handleFromDateChange = (newValue) => {
    if (newValue) {
      const fixedFromDate = dayjs()
        .year(newValue.year())
        .month(3) // April
        .date(1);
      setFromdate(fixedFromDate);

      const fixedToDate = dayjs()
        .year(newValue.year() + 1)
        .month(2) // March
        .date(31);
      setTodate(fixedToDate);
    }
  };

  const handleFetchYearlySummary = async () => {
    try {
      setIsClicked(true);
      setLoading(true);
      if (setYearWise && fromdate && todate) {
        const response = await dispatch(
          fetchYearlyBookwiseReport({ fromdate, todate })
        );
        if (response.payload) {
          setSummary(response.payload);
          setSummaryType("BookwiseYearly");
          logger.info("Filter Applied successfully", {
            filterType: "Yearwise summary-Bookwise All",
            username: user?.username,
            role: user?.role,
          });
        } else {
          logger.warn("Filter applied but no data found", {
            filterType: "Yearwise summary-Bookwise All",
            username: user?.username,
            role: user?.role,
          });
          setSummary([]);
        }
      }
    } catch (error) {
      logger.error("Error occur during Yearwise summary filter", {
        error: error,
      });
      setSummary([]);
    } finally {
      setLoading(false);
    }
  };

  const handlefetchyearwise = async () => {
    try {
      setIsClicked(true);
      setLoading(true);
      const response = await dispatch(fetchAllYearwiseBookwiseReport());
      if (response.payload) {
        setSummary(response.payload);
        setSummaryType("BookwiseYearly");
        logger.info("Filter applied successfully", {
          filterType: "Yearwise Summary - Bookwise",
          username: user?.username,
          role: user?.role,
        });
      } else {
        logger.warn("Filter applied but no data found", {
          filterType: "Yearwise summary-Bookwise",
          username: user?.username,
          role: user?.role,
        });
        setSummary([]);
      }
    } catch (error) {
      logger.error("Error occur during Yearwise Summary Filter", {
        error: error,
      });
      setSummary([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box 
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          marginBottom: 2,
        }}
      >
        <FormControl
          component="fieldset"
          size="small"
          sx={{ minWidth: 120, pl: 1 }}
        >
          <FormLabel component="legend">Set YearWise</FormLabel>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>No</Typography>
            <Switch
              label="Set YearWise"
              name="setYearWise"
              checked={setYearWise}
              onChange={(e) => setSetYearWise(e.target.checked)}
              sx={{ marginTop: 3 }}
            />
            <Typography>Yes</Typography>
          </Stack>
        </FormControl>

        {setYearWise && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* From Date */}
            <DatePicker
              label="From Date"
              views={["year"]}
              value={fromdate}
              onChange={handleFromDateChange}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  readOnly: true,
                  size: "small",
                  fullWidth: true,
                },
              }}
              sx={{ maxWidth: 200 }}
            />

            {/* To Date */}
            <DatePicker
              label="To Date"
              value={todate}
              onChange={(newValue) => setTodate(newValue)}
              format="DD/MM/YYYY"
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  readOnly: true,
                  size: "small",
                  fullWidth: true,
                },
              }}
              sx={{ maxWidth: 150 }}
            />
          </LocalizationProvider>
        )}

        <Button
          variant="contained"
          onClick={setYearWise ? handleFetchYearlySummary : handlefetchyearwise}
          color="primary"
        >
          Fetch Yearly Summary
        </Button>
      </Box>
      {loading ? (
        <Box textAlign="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : summary &&
        (Array.isArray(summary)
          ? summary.length > 0
          : Object.keys(summary).length > 0) ? (
        <YearlySummaryPage data={summary} type={summaryType} />
      ) : (
        isClicked && (
          <Box textAlign="center" mt={2}>
            <Typography fontWeight="bold" color="black">
              No Summary Available
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
};

export default YearlySummaryFilter;
